import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type {
  BusinessTripInput, EdoDecision, EdoType, TimeOffInput, VacationPayload,
  RecruitmentInput, CloseRecruitmentInput, ContractCompany, ItWorksLineDecisionInput, RequestCard,
} from '../../lib/types'

export function useMyRequests() {
  return useQuery({ queryKey: ['edo-requests'], queryFn: api.myRequests })
}

export function useMyTasks() {
  return useQuery({ queryKey: ['edo-tasks'], queryFn: api.myTasks })
}

/** «С моим участием» — документы, где я был инициатором/согласующим/подписантом (в т.ч. обработанные). */
export function useMyParticipating(enabled = true) {
  return useQuery({ queryKey: ['edo-participating'], queryFn: api.myParticipating, enabled })
}

/** Карточка заявки — таймлайн задач, инициатор, подписант, история (GET /requests/:id). */
export function useEdoRequest(id: string) {
  return useQuery({
    queryKey: ['edo-request', id],
    queryFn: () => api.edoRequest(id),
    enabled: !!id,
  })
}

/**
 * Маршрут будущей заявки для формы («кто её согласует» до отправки). Зависит только от типа
 * (+ грейд у подбора, + компания у договора) — держим в кэше подольше. Ошибку глотаем в UI:
 * маршрут справочный, а источник правды и fail-closed проверка остаются на сервере.
 */
export function useRoutePreview(
  type?: EdoType,
  grade?: string,
  onBehalfOfUserId?: string,
  employmentId?: string,
  enabled = true,
  company?: ContractCompany,
) {
  return useQuery({
    queryKey: ['edo-route-preview', type ?? '', grade ?? '', onBehalfOfUserId ?? '', employmentId ?? '', company ?? ''],
    queryFn: () => company
      ? api.routePreview(type!, grade, onBehalfOfUserId, employmentId, company)
      : employmentId
        ? api.routePreview(type!, grade, onBehalfOfUserId, employmentId)
        : api.routePreview(type!, grade, onBehalfOfUserId),
    enabled: !!type && enabled,
    retry: false,
    staleTime: 10 * 60_000,
  })
}

/**
 * Валюты, доступные договору выбранной компании. Набор разный в каждой бухгалтерской базе 1С,
 * и валюта вне справочника базы роняет проводку уже ПОСЛЕ полного круга согласования.
 * Справочники меняются редко — держим ответ свежим полчаса. Ошибку не ретраим: молчание зеркала
 * не должно мешать заполнять форму, пустой ответ читается как «ограничивать нечем».
 */
export function useContractCurrencies(company?: string) {
  return useQuery({
    queryKey: ['edo-contract-currencies', company ?? ''],
    queryFn: () => api.contractCurrencies(company!),
    enabled: !!company,
    retry: false,
    staleTime: 30 * 60_000,
  })
}

/** Страны справочника 1С базы компании — для зарубежного контрагента в форме договора. */
export function useContractCountries(company?: string) {
  return useQuery({
    queryKey: ['edo-contract-countries', company ?? ''],
    queryFn: () => api.contractCountries(company!),
    enabled: !!company,
    retry: false,
    // Короче, чем у валют: страну, которую бухгалтерия добавила в 1С, юрист ждёт увидеть сразу.
    staleTime: 5 * 60_000,
  })
}

/**
 * Мои ЭДО-роли (справочник должностей + оверрайды) — гейт UI подачи «от имени» (роль proxy).
 * Меняются только назначением в админке → долгий кэш; ошибка = ролей нет, пикер скрыт.
 */
export function useMyEdoRoles() {
  return useQuery({
    queryKey: ['edo-my-roles'],
    queryFn: api.myEdoRoles,
    retry: false,
    staleTime: 10 * 60_000,
  })
}

/**
 * Контрагент по ВÖİN для карточки договора (ТЗ-2026-015 §7.2): согласующему юристу нужен
 * риск-статус, а не только номер. Проверка справочная — 404/503 глотаем в UI, ничего не
 * блокируем; на бэке карточка сама себе кэш на сутки, поэтому лишних походов в госпортал нет.
 */
export function useCounterparty(voen?: string) {
  return useQuery({
    queryKey: ['counterparty', voen ?? ''],
    queryFn: () => api.lookupCounterparty(voen!),
    enabled: !!voen && /^\d{10}$/.test(voen),
    retry: false,
    staleTime: 10 * 60_000,
  })
}

/** Остаток месячного лимита отгулов (для формы подачи). 400 без 1С → retry off, обрабатываем в UI.
 *  period (YYYY-MM) — месяц выбранной даты; без него бэк отдаёт текущий месяц. */
export function useTimeOffBalance(period?: string, employmentId?: string, onBehalfOfUserId?: string, enabled = true) {
  return useQuery({
    queryKey: ['edo-timeoff-balance', period ?? 'current', employmentId ?? '', onBehalfOfUserId ?? ''],
    queryFn: () => employmentId || onBehalfOfUserId ? api.timeOffBalance(period, employmentId, onBehalfOfUserId) : api.timeOffBalance(period),
    enabled,
    retry: false,
    staleTime: 60_000,
  })
}

/** Справочник причин отсутствия (dropdown «Причина»). Меняется редко → держим в кэше. */
export function useTimeOffReasons() {
  return useQuery({
    queryKey: ['edo-timeoff-reasons'],
    queryFn: api.timeOffReasons,
    staleTime: 60 * 60_000,
  })
}

/** Отгул — цепочка рук → (Рук.СП) → HR, без подписи GD. */
export function useCreateTimeOff(onDone: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: TimeOffInput) => api.createTimeOff(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      qc.invalidateQueries({ queryKey: ['edo-timeoff-balance'] })
      onDone()
    },
  })
}

/** «Напомнить» текущему согласующему (POST /requests/:id/remind → 204). Только инициатор. */
export function useRemindRequest() {
  return useMutation({
    mutationFn: (id: string) => api.remindRequest(id),
  })
}

/** Остаток отпускных дней из 1С для формы (ФТ-01/КП-01). 1С недоступен → графа скрыта, submit не блокируем. */
export function useVacationBalance(asOf?: string, employmentId?: string, onBehalfOfUserId?: string, enabled = true) {
  return useQuery({
    queryKey: ['edo-vacation-balance', asOf ?? '', employmentId ?? '', onBehalfOfUserId ?? ''],
    queryFn: () => employmentId || onBehalfOfUserId ? api.vacationBalance(asOf, employmentId, onBehalfOfUserId) : api.vacationBalance(asOf),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  })
}

export function useCreateVacation(onDone: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: VacationPayload) => api.createVacation(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      onDone()
    },
  })
}

/**
 * Направления командировок (страна/город + суточные из норм 1С) для пикера.
 * Он же — единственный источник нормы для расчёта (решение «зеркально», 17.07.2026):
 * портальный справочник ставок и хук `usePerDiemRates` удалены вместе с ручкой на бэке.
 */
export function useTripDestinations(enabled = true) {
  return useQuery({
    queryKey: ['edo-trip-destinations'],
    queryFn: () => api.tripDestinations(),
    enabled,
    staleTime: 60 * 60_000,
    retry: false, // нет зеркала норм → пикер просто пуст
  })
}

/** Командировка (ТЗ-2026-003) — рук → рук.СП → HR → CEO → приказ (HR) → CFO → начисление. */
export function useCreateBusinessTrip(onDone: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: BusinessTripInput) => api.createBusinessTrip(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      onDone()
    },
  })
}

// ── Подбор персонала (ТЗ-2026-011) ──

/** Справочник грейдов вакансии (радио-группа «Уровень должности»). Меняется редко → кэш 1 ч. */
export function useRecruitmentGrades() {
  return useQuery({
    queryKey: ['edo-recruitment-grades'],
    queryFn: api.recruitmentGrades,
    staleTime: 60 * 60_000,
  })
}

/** Дашборд ОИ/HR: заявки+фильтры+статистика. 403 не-HR → retry off (скрываем в UI). */
export function useRecruitmentDashboard(f?: { department?: string; grade?: string; status?: string }) {
  return useQuery({
    queryKey: ['edo-recruitment-dashboard', f ?? {}],
    queryFn: () => api.recruitmentDashboard(f),
    retry: false,
    staleTime: 30_000,
  })
}

/** Создать заявку на подбор. Портальный процесс без 1С. */
export function useCreateRecruitment(onDone: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: RecruitmentInput) => api.createRecruitment(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      onDone()
    },
  })
}

/** Закрыть заявку с данными кандидата (резюме обязательно). Инвалидирует карточку + списки. */
export function useCloseRecruitment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CloseRecruitmentInput }) =>
      api.closeRecruitment(id, data),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: ['edo-request', id] })
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      qc.invalidateQueries({ queryKey: ['edo-tasks'] })
    },
  })
}

/** Отозвать/отменить заявку (причина обязательна). */
export function useCancelRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      api.cancelRequest(id, comment),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: ['edo-request', id] })
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      qc.invalidateQueries({ queryKey: ['edo-tasks'] })
    },
  })
}

/** Кадровик правит приказы закрытого шага приказа. Инвалидирует карточку и списки. */
export function useUpdateOrderScans() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, uploadIds }: { id: string; uploadIds: string[] }) =>
      api.updateOrderScans(id, uploadIds),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: ['edo-request', id] })
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
    },
  })
}

/** Добавить согласующего в идущий документ. Инвалидирует карточку, списки и задачи. */
export function useAddApprover() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, userId, position }: { id: string; userId: string; position: 'next' | 'end' }) =>
      api.addApprover(id, userId, position),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: ['edo-request', id] })
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      qc.invalidateQueries({ queryKey: ['edo-tasks'] })
    },
  })
}

/**
 * Решения согласующего по своим строкам модулей IT_WORKS. Последнее решение закрывает этап и двигает
 * заявку — свежими должны быть карточка, «Мои задачи» и список заявок.
 *
 * Ответ — карточка после решения: кладём её в кэш сразу, иначе до перечитывания строка показывает
 * живые кнопки на старых данных (второй клик). Ответ собран без полей смотрящего (route, canCancel,
 * actingFor… — их считает только GET), поэтому сливаем поверх старой, а не заменяем; перечитывание
 * следом всё равно идёт.
 */
export function useDecideItWorksLines() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, decisions }: { id: string; decisions: ItWorksLineDecisionInput[] }) =>
      api.decideItWorksLines(id, decisions),
    onSuccess: (res, { id }) => {
      qc.setQueryData<RequestCard>(['edo-request', id], (old) => (old ? { ...old, ...res } : res))
      qc.invalidateQueries({ queryKey: ['edo-request', id] })
      qc.invalidateQueries({ queryKey: ['edo-tasks'] })
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
    },
  })
}

/** Бухгалтер: начислить отпускные (ТЗ-2026-004 шаг 11). Инвалидирует карточку + список. */
export function useAccrueVacation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, vacationPay }: { id: string; vacationPay?: number }) =>
      api.accrueVacation(id, vacationPay),
    onSuccess: (_res, { id }) => {
      qc.invalidateQueries({ queryKey: ['edo-request', id] })
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
    },
  })
}

export function useDecideTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      decision,
      comment,
      scanUploadIds,
      // К1: правка документа при переотправке с доработки — исправленные поля payload
      // и новые вложения. Бэк принимает их только на шаге REWORK с decision='resubmit'.
      revise,
      // Срок действия договора — приходит на шаге автора SIGNED_COPY, вместе со сканом.
      dates,
      // Замещающий: HR меняет его при согласовании отпуска (string — назначить, null — снять).
      deputyId,
    }: {
      id: string
      decision: EdoDecision
      comment?: string
      scanUploadIds?: string[]
      revise?: { payload?: Record<string, unknown>; uploadIds?: string[] }
      dates?: { dateFrom: string; dateTo: string }
      deputyId?: string | null
    }) => api.decideTask(id, decision, comment, scanUploadIds, revise, dates, deputyId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['edo-tasks'] })
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      qc.invalidateQueries({ queryKey: ['unread'] })
      // Открытая карточка: маршрут, история и правки на шаге (напр. замещающий от HR) — свежие.
      qc.invalidateQueries({ queryKey: ['edo-request'] })
    },
  })
}

/**
 * Взять групповую задачу на себя (pull). Обновляет «Мои задачи» (у остальных членов группы
 * задача скрывается) и открытую карточку заявки (кнопка в дровере переключается на «Вернуть»).
 */
export function useClaimTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.claimTask(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['edo-tasks'] })
      qc.invalidateQueries({ queryKey: ['edo-request'] })
    },
  })
}

/** Кандидаты для пикера назначения. Тянем только когда задача групповая и открыта. */
export function useTaskCandidates(taskId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['edo-task-candidates', taskId],
    queryFn: () => api.taskCandidates(taskId!),
    enabled: !!taskId && enabled,
  })
}

/** Push-назначение исполнителя (лид/admin). Инвалидирует карточку и список кандидатов. */
export function useAssignTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, userId }: { taskId: string; userId: string }) =>
      api.assignTask(taskId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['edo-tasks'] })
      qc.invalidateQueries({ queryKey: ['edo-request'] })
      qc.invalidateQueries({ queryKey: ['edo-task-candidates'] })
    },
  })
}

/** Вернуть задачу в группу (снять с себя) — задача снова видна всем членам группы. */
export function useUnclaimTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.unclaimTask(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['edo-tasks'] })
      qc.invalidateQueries({ queryKey: ['edo-request'] })
    },
  })
}
